/**
 * KanbanBoard.tsx
 *
 * Rendered inside Dashboard.tsx when activeTab === 'kanban'.
 * Receives token as a prop (matching Dashboard's auth pattern) and
 * an onDealClick callback so clicking a card switches to the Deal Bank
 * tab with that deal already expanded.
 *
 * Uses inline styles throughout to match Coast's design system —
 * same design tokens (gold, bg, surface, etc.) as Dashboard.tsx.
 *
 * Drag-and-drop uses the native HTML5 DnD API with refs for in-flight
 * state so dragover (which fires at ~60fps) never triggers a re-render.
 * Only two state updates happen per drag: dragStart and dragDrop.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';

// ── Types ────────────────────────────────────────────────────────────────────

interface KanbanBoardProps {
  token: string;
  /** Called when a deal card is clicked. Parent switches to Deal Bank and expands that deal. */
  onDealClick?: (dealId: number) => void;
}

interface DealCard {
  id: number;
  deal_name: string;
  property_address: string;
  asset_type: string;
  purchase_price: number | null;
  status: string;
  pipeline_stage: string;
  pipeline_order: number;
  score: number | null;
}

type Board = Record<string, DealCard[]>;

// Five stages in left-to-right display order.
const STAGES = ['Prospecting', 'LOI', 'Due Diligence', 'Under Contract', 'Closed'];

// Each stage gets a distinct accent color — all from the coast palette
const STAGE_ACCENT: Record<string, string> = {
  'Prospecting':    '#2d7fa0',
  'LOI':            '#1a6688',
  'Due Diligence':  '#b8956a',
  'Under Contract': '#12526e',
  'Closed':         '#0d3d5c',
};

const API = 'https://coast-terminal-production.up.railway.app';

const fmtPrice = (n: number | null) =>
  n ? '$' + (n / 1_000_000).toFixed(1) + 'M' : '—';

// ── Score ring — tiny circular SVG arc showing the deal's score ──────────────
// Uses stroke-dashoffset technique: fills (score/100) * circumference of arc.

const ScoreRing: React.FC<{ score: number | null }> = ({ score }) => {
  const r = 13;
  const circ = 2 * Math.PI * r;
  const fill = score !== null ? (score / 100) * circ : 0;
  const color =
    score === null ? '#2d7fa0' :
    score >= 70    ? '#1A7A4A' :
    score >= 50    ? '#C9A96E' : '#CC2200';

  return (
    <svg width="34" height="34" viewBox="0 0 34 34" style={{ flexShrink: 0 }}>
      {/* Background ring */}
      <circle cx="17" cy="17" r={r} fill="none" stroke="#c4a870" strokeWidth="2.5" />
      {/* Filled arc, starting from top (rotate -90°) */}
      <circle
        cx="17" cy="17" r={r}
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeDasharray={`${fill} ${circ - fill}`}
        strokeLinecap="round"
        transform="rotate(-90 17 17)"
      />
      <text x="17" y="17" dominantBaseline="middle" textAnchor="middle"
        fontSize="8" fontWeight="700" fill="#0d3d5c">
        {score !== null ? Math.round(score) : '–'}
      </text>
    </svg>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const KanbanBoard: React.FC<KanbanBoardProps> = ({ token, onDealClick }) => {

  // Design tokens — mirrors Dashboard.tsx exactly so the board feels native
  const gold = '#b8956a';
  const bg = '#ffffff';
  const surface = '#fafaf8';
  const surfaceRaised = '#f2ede6';
  const surfaceHigh = '#e6ddd4';
  const border = '#c4a870';
  const borderLight = '#e6ddd4';
  const textPrimary = '#0d3d5c';
  const textSecondary = '#12526e';
  const textMuted = '#2d7fa0';
  const green = '#1A7A4A';
  const red = '#CC2200';
  const sl: React.CSSProperties = { fontSize: '9px', color: gold, letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '6px', fontWeight: 700 };

  const headers = { Authorization: `Bearer ${token}` };

  // Board state — the source of truth for what renders
  const [board, setBoard] = useState<Board>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  // draggingStateId drives the visual "opacity 0.4" effect on the card being dragged.
  // It lives in state (not ref) because it needs to trigger a re-render on drag start/end.
  const [draggingStateId, setDraggingStateId] = useState<number | null>(null);

  // dragTargetStage drives the column highlight. Updated when the dragged card
  // enters a new column — much less frequent than every 60fps dragover event.
  const [dragTargetStage, setDragTargetStage] = useState<string | null>(null);

  // These refs hold drag state without triggering re-renders on every dragover event.
  const draggingId = useRef<number | null>(null);
  const draggingFromStage = useRef<string | null>(null);
  const dragOverStage = useRef<string | null>(null);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchBoard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/kanban/board`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setBoard(data.board);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  // ── Toast ──────────────────────────────────────────────────────────────────

  const showToast = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Drag handlers ──────────────────────────────────────────────────────────

  const handleDragStart = (e: React.DragEvent, dealId: number, fromStage: string) => {
    draggingId.current = dealId;
    draggingFromStage.current = fromStage;
    setDraggingStateId(dealId);
    e.dataTransfer.effectAllowed = 'move';
    // setData is required for Firefox compatibility
    e.dataTransfer.setData('text/plain', String(dealId));
  };

  const handleDragEnd = () => {
    draggingId.current = null;
    draggingFromStage.current = null;
    dragOverStage.current = null;
    setDraggingStateId(null);
    setDragTargetStage(null);
  };

  const handleDragOver = (e: React.DragEvent, stage: string) => {
    // preventDefault is required to allow drop
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    // Only update state when we enter a NEW column, not on every 60fps event
    if (dragOverStage.current !== stage) {
      dragOverStage.current = stage;
      setDragTargetStage(stage);
    }
  };

  const handleDrop = async (e: React.DragEvent, toStage: string) => {
    e.preventDefault();
    setDragTargetStage(null);

    const movedDealId = draggingId.current;
    const fromStage = draggingFromStage.current;
    if (!movedDealId || !fromStage || fromStage === toStage) return;

    const movedDeal = (board[fromStage] || []).find(d => d.id === movedDealId);
    if (!movedDeal) return;

    const prevBoard = board; // Saved for rollback if API call fails

    // ── Optimistic update: apply new board state immediately ──────────────
    // The user sees the move happen instantly; the API call happens in the background.
    const newBoard: Board = {};
    for (const stage of STAGES) newBoard[stage] = [...(board[stage] || [])];

    newBoard[fromStage] = newBoard[fromStage].filter(d => d.id !== movedDealId);
    const updatedDeal = { ...movedDeal, pipeline_stage: toStage, pipeline_order: newBoard[toStage].length };
    newBoard[toStage] = [...newBoard[toStage], updatedDeal];

    // Reindex both affected columns so pipeline_order values are contiguous (0, 1, 2...)
    newBoard[fromStage] = newBoard[fromStage].map((d, i) => ({ ...d, pipeline_order: i }));
    newBoard[toStage]   = newBoard[toStage].map((d, i) => ({ ...d, pipeline_order: i }));
    setBoard(newBoard);

    // Build the full list of shifted cards to send to the backend
    const shiftedDeals = [
      ...newBoard[toStage].filter(d => d.id !== movedDealId).map(d => ({ deal_id: d.id, order: d.pipeline_order })),
      ...newBoard[fromStage].map(d => ({ deal_id: d.id, order: d.pipeline_order })),
    ];

    try {
      const res = await fetch(`${API}/api/kanban/move`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deal_id: movedDealId,
          new_stage: toStage,
          new_order: updatedDeal.pipeline_order,
          shifted_deals: shiftedDeals,
        }),
      });
      if (!res.ok) throw new Error(`API error ${res.status}`);
      showToast(`Moved to ${toStage}`, 'success');
    } catch (err: any) {
      // Rollback to the previous board state on failure
      setBoard(prevBoard);
      showToast(`Failed to save: ${err.message}`, 'error');
    }

    draggingId.current = null;
    draggingFromStage.current = null;
    setDraggingStateId(null);
  };

  // ── Computed stats ─────────────────────────────────────────────────────────

  const totalDeals = Object.values(board).reduce((sum, arr) => sum + arr.length, 0);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ padding: '60px 20px', textAlign: 'center', color: textMuted, fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase' }}>
        Loading Pipeline...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ color: red, fontSize: '12px', marginBottom: '8px' }}>Failed to load pipeline</div>
        <div style={{ color: textMuted, fontSize: '11px', marginBottom: '16px' }}>{error}</div>
        <button onClick={fetchBoard} style={{ backgroundColor: surfaceHigh, border: `1px solid ${borderLight}`, color: textSecondary, padding: '8px 20px', fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 700 }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ fontSize: '9px', color: gold, letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '8px', fontWeight: 700, fontFamily: 'sans-serif' }}>Pipeline Management</div>
        <div style={{ fontSize: '32px', fontWeight: 600, color: textPrimary, fontFamily: "'Fraunces', Georgia, serif", marginBottom: '6px' }}>Deal Pipeline</div>
        <div style={{ fontSize: '15px', color: textMuted, fontFamily: "'EB Garamond', Georgia, serif" }}>
          {totalDeals} deal{totalDeals !== 1 ? 's' : ''} across {STAGES.length} stages · Drag cards to advance deals · Click any card to open in Deal Bank
        </div>
      </div>

      {/* Stage summary bar */}
      <div style={{ display: 'flex', gap: '1px', marginBottom: '24px', backgroundColor: border }}>
        {STAGES.map(stage => (
          <div key={stage} style={{ flex: 1, backgroundColor: '#fafaf8', padding: '16px 8px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 700, color: STAGE_ACCENT[stage] || gold, lineHeight: 1, fontFamily: "'Fraunces', Georgia, serif" }}>
              {(board[stage] || []).length}
            </div>
            <div style={{ fontSize: '8px', color: textMuted, letterSpacing: '2px', textTransform: 'uppercase', marginTop: '6px', fontWeight: 700, fontFamily: 'sans-serif' }}>{stage}</div>
          </div>
        ))}
      </div>

      {/* Board — horizontally scrollable so all 5 columns fit on any screen */}
      <div style={{ overflowX: 'auto', marginLeft: '-4px', marginRight: '-4px', paddingBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '10px', padding: '4px', minWidth: 'max-content' }}>
          {STAGES.map(stage => {
            const deals = board[stage] || [];
            const isDragTarget = dragTargetStage === stage;
            const accent = STAGE_ACCENT[stage] || gold;

            return (
              <div
                key={stage}
                onDragOver={(e) => handleDragOver(e, stage)}
                onDrop={(e) => handleDrop(e, stage)}
                style={{
                  width: '252px',
                  minWidth: '252px',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: isDragTarget ? 'rgba(201,169,110,0.03)' : surface,
                  border: `1px solid ${isDragTarget ? borderLight : border}`,
                  borderTop: `3px solid ${accent}`,
                  transition: 'border-color 0.15s, background-color 0.15s',
                }}
              >
                {/* Column header */}
                <div style={{ padding: '13px 14px 10px', borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#fafaf8' }}>
                  <div style={{ fontSize: '9px', fontWeight: 800, color: textSecondary, letterSpacing: '1.5px', textTransform: 'uppercase' }}>{stage}</div>
                  <div style={{ fontSize: '10px', fontWeight: 900, color: accent, backgroundColor: surfaceHigh, border: `1px solid ${border}`, padding: '2px 7px', minWidth: '22px', textAlign: 'center' }}>
                    {deals.length}
                  </div>
                </div>

                {/* Cards area */}
                <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: '7px', flex: 1, minHeight: '120px' }}>

                  {/* Empty state */}
                  {deals.length === 0 && (
                    <div style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: `1px dashed ${isDragTarget ? borderLight : border}`,
                      padding: '28px 12px', margin: '4px',
                    }}>
                      <div style={{ fontSize: '10px', color: textMuted, textAlign: 'center' }}>
                        {isDragTarget ? 'Drop here' : 'No deals'}
                      </div>
                    </div>
                  )}

                  {/* Deal cards */}
                  {deals.map(deal => {
                    const isDragging = draggingStateId === deal.id;
                    return (
                      <div
                        key={deal.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, deal.id, stage)}
                        onDragEnd={handleDragEnd}
                        onClick={() => onDealClick?.(deal.id)}
                        title="Click to open in Deal Bank"
                        style={{
                          backgroundColor: isDragging ? bg : surfaceRaised,
                          border: `1px solid ${isDragging ? gold : border}`,
                          borderLeft: `2px solid ${accent}`,
                          padding: '11px 12px',
                          cursor: 'grab',
                          opacity: isDragging ? 0.4 : 1,
                          transition: 'opacity 0.15s, border-color 0.15s',
                          userSelect: 'none',
                        }}
                      >
                        {/* Card top: score ring + name + address */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '9px', marginBottom: '9px' }}>
                          <ScoreRing score={deal.score} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '2px' }}>
                              {deal.deal_name}
                            </div>
                            <div style={{ fontSize: '10px', color: textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {deal.property_address || 'No address'}
                            </div>
                          </div>
                        </div>

                        {/* Card footer: asset type badge + price */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {deal.asset_type && (
                            <div style={{ fontSize: '8px', color: textSecondary, backgroundColor: surfaceHigh, border: `1px solid ${border}`, padding: '2px 6px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', flexShrink: 0 }}>
                              {deal.asset_type}
                            </div>
                          )}
                          <div style={{ fontSize: '10px', color: gold, fontWeight: 800, fontFamily: 'monospace', marginLeft: 'auto' }}>
                            {fmtPrice(deal.purchase_price)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Toast notification */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 200,
          padding: '11px 18px', fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px',
          backgroundColor: toast.type === 'success' ? 'rgba(26,122,74,0.97)' : 'rgba(204,34,0,0.97)',
          border: `1px solid ${toast.type === 'success' ? green : red}`,
          color: textPrimary, boxShadow: '0 4px 28px rgba(0,0,0,0.5)',
        }}>
          {toast.type === 'success' ? '✓ ' : '✗ '}{toast.msg}
        </div>
      )}
    </div>
  );
};

export default KanbanBoard;